import {useCallback, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {supabase} from '../config/supabase'
import {useAuth} from '../contexts/AuthContext'

export const useAnalytics = () => {
  const {user} = useAuth()
  const location = useLocation()

  // Track pageview when location changes
  useEffect(() => {
    trackPageview()
  }, [location])

  const trackEvent = useCallback(async (eventName, properties = {}) => {
    try {
      // Enhanced properties with page context
      const enhancedProperties = {
        ...properties,
        page_url: window.location.href,
        page_path: location.pathname,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }

      // Log to console for development
      console.log('Analytics Event:', eventName, enhancedProperties)

      // Store in Supabase (optional - uncomment if you want to store analytics)
      if (user) {
        await supabase
          .from('analytics_events')
          .insert([{
            user_id: user.id,
            event_name: eventName,
            properties: enhancedProperties,
            page_url: window.location.href,
            page_path: location.pathname,
            timestamp: new Date().toISOString()
          }])
      }
    } catch (error) {
      console.error('Error tracking event:', error)
    }
  }, [user, location])

  const trackPageview = useCallback(async () => {
    const pageData = {
      page_url: window.location.href,
      page_path: location.pathname,
      page_title: document.title,
      referrer: document.referrer
    }

    await trackEvent('page_view', pageData)
  }, [location, trackEvent])

  const trackCTAClick = useCallback(async (ctaName, ctaLocation, destination = '') => {
    await trackEvent('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      destination: destination,
      page_path: location.pathname
    })
  }, [trackEvent, location])

  const trackFormSubmission = useCallback(async (formName, formData = {}) => {
    await trackEvent('form_submission', {
      form_name: formName,
      form_data: formData,
      page_path: location.pathname
    })
  }, [trackEvent, location])

  const trackCourseInteraction = useCallback(async (action, courseId, courseTitle) => {
    await trackEvent('course_interaction', {
      action: action,
      course_id: courseId,
      course_title: courseTitle,
      page_path: location.pathname
    })
  }, [trackEvent, location])

  const trackSearch = useCallback(async (searchTerm, resultsCount = 0) => {
    await trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      page_path: location.pathname
    })
  }, [trackEvent, location])

  // Alias for consistency
  const track = trackEvent

  return {
    trackEvent,
    track,
    trackPageview,
    trackCTAClick,
    trackFormSubmission,
    trackCourseInteraction,
    trackSearch
  }
}